import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { applySeoHead } from "@/seo/head";
import { applySchemas, websiteSchema, articleSchema, breadcrumbSchema, faqSchema, type FaqItem } from "@/seo/schema";
import { SITE_URL, SITE_NAME } from "@/seo/site";
import { articles } from "@/mocks/articles";
import { heroImage } from "@/mocks/images";

const HOME_TITLE = "AllChinaBuy Spreadsheet 2026 | Finds, QC, Shipping & Guides";
const HOME_DESCRIPTION =
  "Explore the AllChinaBuy Spreadsheet with organized product finds, category links, QC references, shipping guides, warehouse tips and buyer resources for smarter AllChinaBuy shopping.";
const HOME_KEYWORDS =
  "AllChinaBuy spreadsheet, AllChinaBuy finds, AllChinaBuy shipping, AllChinaBuy QC, AllChinaBuy guide";

export default function SeoManager() {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";

  useEffect(() => {
    const path = location.pathname;

    // Home page
    if (path === "/" || path === "") {
      applySeoHead({
        title: HOME_TITLE,
        description: HOME_DESCRIPTION,
        keywords: HOME_KEYWORDS,
        canonical: `${SITE_URL}/`,
        ogUrl: `${SITE_URL}/`,
        ogImage: heroImage,
        twitterImage: heroImage,
      });
      applySchemas([
        { id: "schema-website", data: websiteSchema() },
      ]);
      return;
    }

    // Article page: /article/:slug
    const articleMatch = path.match(/^\/article\/([^/]+)/);
    if (articleMatch) {
      const slug = articleMatch[1];
      const article = articles.find((a) => a.slug === slug);
      if (!article) return;

      const title = t(`articles.${slug}.metaTitle`, article.slug);
      const description = t(`articles.${slug}.metaDescription`, "");
      const faq = t(`articles.${slug}.faq`, { returnObjects: true }) as unknown as FaqItem[];

      const url = `${SITE_URL}/article/${slug}`;

      applySeoHead({
        title,
        description,
        keywords: `${slug.replace(/-/g, " ")}, AllChinaBuy spreadsheet, AllChinaBuy guide`,
        canonical: url,
        ogTitle: title,
        ogDescription: description,
        ogUrl: url,
        ogImage: article.image,
        twitterImage: article.image,
      });

      const schemas: { id: string; data: Record<string, unknown> }[] = [
        {
          id: "schema-article",
          data: articleSchema({
            title,
            description,
            image: article.image,
            url,
            datePublished: "2026-08-01",
            dateModified: "2026-08-24",
            author: SITE_NAME,
            language: lang,
          }),
        },
        {
          id: "schema-breadcrumb",
          data: breadcrumbSchema([
            { name: SITE_NAME, url: `${SITE_URL}/` },
            { name: title, url },
          ]),
        },
      ];

      if (Array.isArray(faq) && faq.length > 0) {
        schemas.push({ id: "schema-faq", data: faqSchema(faq) });
      }

      applySchemas(schemas);
      return;
    }
  }, [location.pathname, t, lang]);

  return null;
}